/**
 * Shared constants for show details
 */

type MuiChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

/**
 * HOLD status -> MUI colour. Mirrors Hold.STATUS_CHOICES in
 * apps/bookings/models.py:62-69.
 *
 * These are NOT show statuses. The two enums share only the value "confirmed",
 * so applying these maps to a Show renders raw lowercase text for every other
 * status. Use SHOW_STATUS_* below for shows.
 */
export const STATUS_COLORS: Record<string, MuiChipColor> = {
  requested: 'info',
  declined: 'default',
  pending: 'warning',
  standby: 'default',
  challenged: 'error',
  confirmed: 'success',
  released: 'default',
  expired: 'default',
  cancelled: 'default',
};

/**
 * HOLD status -> display label. See the note on STATUS_COLORS.
 */
export const STATUS_LABELS: Record<string, string> = {
  requested: 'Awaiting host acceptance',
  declined: 'Declined by host',
  pending: 'Pending',
  standby: 'Standby',
  challenged: 'Challenged',
  confirmed: 'Confirmed',
  released: 'Released',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

/**
 * SHOW status -> MUI colour. Mirrors Show.STATUS_CHOICES in
 * apps/shows/models.py:64-70, and matches the legacy component's mapping.
 */
export const SHOW_STATUS_COLORS: Record<string, MuiChipColor> = {
  tentative: 'default',
  confirmed: 'info',
  completed: 'success',
  settled: 'success',
  cancelled: 'error',
};

/**
 * SHOW status -> display label.
 */
export const SHOW_STATUS_LABELS: Record<string, string> = {
  tentative: 'Tentative',
  confirmed: 'Confirmed',
  completed: 'Completed',
  settled: 'Settled',
  cancelled: 'Cancelled',
};

/**
 * Show type -> display label. Mirrors Show.TYPE_CHOICES in
 * apps/shows/models.py:72-81 exactly - all eight values, no invented ones.
 */
export const SHOW_TYPE_LABELS: Record<string, string> = {
  concert: 'Concert',
  festival: 'Festival',
  comedy: 'Comedy Show',
  theater: 'Theater',
  dance: 'Dance Performance',
  spoken_word: 'Spoken Word',
  private_event: 'Private Event',
  other: 'Other',
};

/**
 * Position colors for hold badges
 */
export const POSITION_COLORS: Record<number, string> = {
  1: '#FFD700', // Gold
  2: '#C0C0C0', // Silver
  3: '#CD7F32', // Bronze
  4: '#E8E8E8', // Light gray
};

/**
 * Position text labels
 */
export const POSITION_LABELS: Record<number, string> = {
  1: '1st',
  2: '2nd',
  3: '3rd',
  4: '4th',
};
